import { Injectable, inject } from '@angular/core';
import { TrackingService } from './tracking.service';
import { ITrackingEventSnowplow } from '../models/tracking/tracking-event.model';
import { Queue } from '../models/queue.model';
import { TrackingEvents } from '../data/tracking-events.data';
import {
    ConsentType,
    CookieConsentService,
    IConsent,
    IConsentConfig,
} from './cookie-consent.service';
import {
    trackConsentAllow,
    trackConsentDeny,
    trackConsentSelected,
    trackConsentWithdrawn,
} from '@snowplow/browser-plugin-enhanced-consent';

@Injectable({
    providedIn: 'root',
})
export class SnowplowService extends TrackingService {
    private _consentService = inject(CookieConsentService);

    private _eventQueue = new Queue<ITrackingEventSnowplow>();

    constructor() {
        super();

        this._hasConsent.subscribe(() => {
            this.onHasConsent();
        });
    }

    track(event: string | ITrackingEventSnowplow, data?: unknown): void {
        if (!this.Window) {
            return;
        }

        if (typeof event !== 'string') {
            this.enqueue(event);

            return;
        }

        if (!data) {
            console.error('Tracking data is undefined!');

            return;
        }

        if (this.Window.top && this.Window.self !== this.Window.top) {
            this.Window.top.postMessage({ trackingSnowplow: { name: event, data: data } }, '*');

            return;
        }

        const e: ITrackingEventSnowplow = TrackingEvents.snowplow[event](data);
        this.enqueue(e);
    }

    override processQueue(): void {
        while (!this._eventQueue.isEmpty()) {
            const event = this._eventQueue.dequeue();
            if (event) {
                this.enqueue(event);
            }
        }
    }

    private enqueue(e: ITrackingEventSnowplow): void {
        if (!this.hasConsent) {
            this._eventQueue.enqueue(e);

            return;
        }

        e.fn(e.data);
    }

    private onHasConsent() {
        if (!this.Window || (this.Window.top && this.Window.self !== this.Window.top)) {
            return;
        }

        const cfg: IConsentConfig = {
            necessary: false,
            preferences: false,
            statistics: false,
            marketing: false,
        };

        if (this.Window.Cookiebot && this.Window.Cookiebot.consent) {
            cfg.necessary = this.Window.Cookiebot.consent.necessary;
            cfg.preferences = this.Window.Cookiebot.consent.preferences;
            cfg.statistics = this.Window.Cookiebot.consent.statistics;
            cfg.marketing = this.Window.Cookiebot.consent.marketing;
        }

        const res = this._consentService.getConsent(cfg);
        this.trackConsent(res);
    }

    private trackConsent(consent: IConsent) {
        switch (consent.type) {
            case ConsentType.Allow:
                trackConsentAllow({
                    basisForProcessing: 'consent',
                    consentUrl: 'https://universal-robots.com',
                    consentVersion: '1.0',
                    consentScopes: consent.scopes,
                    domainsApplied: ['https://universal-robots.com'],
                });

                break;
            case ConsentType.Withdrawn:
                trackConsentWithdrawn({
                    basisForProcessing: 'consent',
                    consentUrl: 'https://universal-robots.com',
                    consentVersion: '1.0',
                    consentScopes: consent.scopes,
                    domainsApplied: ['https://universal-robots.com'],
                });

                break;
            case ConsentType.Selected:
                trackConsentSelected({
                    basisForProcessing: 'consent',
                    consentUrl: 'https://universal-robots.com',
                    consentVersion: '1.0',
                    consentScopes: consent.scopes,
                    domainsApplied: ['https://universal-robots.com'],
                });

                break;
            case ConsentType.Deny:
                trackConsentDeny({
                    basisForProcessing: 'consent',
                    consentUrl: 'https://universal-robots.com',
                    consentVersion: '1.0',
                    consentScopes: consent.scopes,
                    domainsApplied: ['https://universal-robots.com'],
                });

                break;
        }
    }
}
